import React from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";
import { COMPANY, TEAM, VALUES } from "../lib/siteData";

export default function About() {
  return (
    <main data-testid="page-about" className="pt-32 md:pt-40 bg-[#FCFCFA]">
      {/* Title */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-10">
          <ScrollReveal className="md:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">About Cape Maclear Holdings</span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] text-[#1A1D20]">
              A visionary master developer for a new African century.
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.15} className="md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-[#5C636A] text-[15px] md:text-[17px] leading-relaxed">
              Founded to plan, develop and execute large-scale infrastructure,
              CMHL integrates a new international airport, an International
              Financial Centre, luxury hotels and wellness resorts, tourism,
              logistics, agriculture, energy and smart-city technology into a
              single, coherent city ecosystem on the shores of Lake Malawi.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Hero image */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="relative aspect-[16/7] overflow-hidden">
          <img
            alt="Cape Maclear International Airport"
            src="https://customer-assets.emergentagent.com/job_maclear-holdings/artifacts/npy9hjsp_airport.png"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-[#1A1D20] text-[#FCFCFA] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20">
          <ScrollReveal>
            <p className="overline mb-6">Mission</p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight mb-8">
              Drive long-term growth, deliver sustainable infrastructure, and
              attract global investment — contributing to the UN Sustainable
              Development Goals.
            </h2>
          </ScrollReveal>
          <ScrollReveal>
            <p className="overline mb-6">Vision</p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight mb-8">
              To create Africa's leading integrated smart city and economic
              hub — an international airport, an International Financial
              Centre, world-class hotels and wellness resorts, and a
              destination for business, tourism and sustainable living.
            </h2>
            <p className="text-white/75 text-[15px] leading-relaxed">
              An inclusive community that cherishes diversity, encourages
              creativity and shares the benefits of development with all.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-14">
            <span className="hairline-gold" />
            <span className="overline">Our Values</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {VALUES.map((v, i) => (
              <ScrollReveal key={v.title} delay={i * 0.08}>
                <div className="border-t border-[#E5E5DF] pt-8">
                  <p className="font-serif-display text-3xl text-[#1A1D20]">
                    <span className="text-[#D4AF37]">0{i + 1}</span>
                  </p>
                  <p className="font-serif-display text-2xl mt-3 text-[#1A1D20]">{v.title}</p>
                  <p className="mt-4 text-[14px] leading-relaxed text-[#5C636A]">{v.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-[#F5F5F0] py-24 md:py-32" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <ScrollReveal className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Leadership</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-[#1A1D20]">
              Guided by a founder<br />with a continental vision.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="md:col-span-6 md:col-start-7">
            <div className="border border-[#E5E5DF] bg-white overflow-hidden grid md:grid-cols-5">
              <div className="md:col-span-2 bg-[#F5F5F0]">
                <img
                  src="https://customer-assets.emergentagent.com/job_maclear-holdings/artifacts/cdwsvpzz_bright.jpeg"
                  alt="Mr Bright Malopa — CEO, Cape Maclear Holdings"
                  data-testid="ceo-photo"
                  className="w-full h-full object-cover aspect-[4/5] md:aspect-auto"
                />
              </div>
              <div className="md:col-span-3 p-8 md:p-10">
                <p className="font-serif-display text-3xl md:text-4xl text-[#1A1D20]">
                  {COMPANY.ceo}
                </p>
                <p className="mt-2 uppercase tracking-[0.2em] text-[11px] font-semibold text-[#D4AF37]">
                  Chief Executive Officer
                </p>
                <p className="mt-6 text-[14px] leading-relaxed text-[#5C636A]">
                  Founder and visionary leader behind the Cape Maclear initiative.
                  A forward-thinking executive committed to transformative
                  development — building infrastructure that unlocks opportunity
                  for local communities and global investors alike.
                </p>
                <p className="mt-5 text-[14px] leading-relaxed text-[#5C636A]">
                  He leads a multidisciplinary team spanning infrastructure,
                  finance, engineering, urban planning and strategic investment —
                  fusing local insight with international expertise.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Team — second-to-last banner */}
      <section className="py-24 md:py-32 bg-[#FCFCFA] border-t border-[#E5E5DF]" data-testid="our-team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
            <ScrollReveal className="md:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="hairline-gold" />
                <span className="overline">Our Team</span>
              </div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] text-[#1A1D20]">
                CMHL Management<br />&amp; AE7 Africa.
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1} className="md:col-span-5">
              <p className="text-[#5C636A] text-[15px] md:text-[17px] leading-relaxed">
                A multidisciplinary leadership team combining Malawian insight
                with international development expertise — across finance,
                engineering, design, urban planning and risk.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal>
            <div className="relative border border-[#E5E5DF] bg-white p-3 md:p-5 shadow-sm">
              <img
                src="/team_page.jpg"
                alt="Cape Maclear Holdings management team and AE7 Development Management Africa team"
                data-testid="team-photo-page"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </ScrollReveal>

          <div className="mt-20 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-6">
              <div className="flex items-center gap-4 mb-8">
                <span className="hairline-gold" />
                <span className="overline">CMHL Management</span>
              </div>
              <ul className="divide-y divide-[#E5E5DF]" data-testid="team-list-cmhl">
                {TEAM.cmhl.map((m) => (
                  <li key={m.name} className="py-5 flex items-baseline justify-between gap-4">
                    <div>
                      <p className="font-serif-display text-xl md:text-2xl text-[#1A1D20] leading-tight">
                        {m.name}
                      </p>
                      <p className="mt-1 text-[12px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">
                        {m.title}
                      </p>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C636A] shrink-0 hidden sm:block">
                      CMHL
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-6">
              <div className="flex items-center gap-4 mb-8">
                <span className="hairline-gold" />
                <span className="overline">AE7 Development Mgmt. Africa</span>
              </div>
              <ul className="divide-y divide-[#E5E5DF]" data-testid="team-list-ae7">
                {TEAM.ae7.map((m) => (
                  <li key={m.name} className="py-5 flex items-baseline justify-between gap-4">
                    <div>
                      <p className="font-serif-display text-xl md:text-2xl text-[#1A1D20] leading-tight">
                        {m.name}
                      </p>
                      <p className="mt-1 text-[12px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">
                        {m.title}
                      </p>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C636A] shrink-0 hidden sm:block">
                      AE7
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FCFCFA] py-24 md:py-28 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif-display text-3xl md:text-5xl leading-tight text-[#1A1D20]">
            Want to learn more about Cape Maclear Holdings?
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center bg-[#1A1D20] text-white hover:bg-[#D4AF37] hover:text-[#1A1D20] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
            >
              Get In Touch
            </Link>
            <Link
              to="/services"
              data-testid="about-cta-services"
              className="inline-flex items-center border border-[#1A1D20] text-[#1A1D20] hover:bg-[#1A1D20] hover:text-white transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
            >
              See Our Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
